import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  GridLegacy as Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Stack,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
  Checkbox,
  FormControlLabel,
  Toolbar,
  Fab,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  ShowChart as ShowChartIcon,
  Assessment as AssessmentIcon,
  SelectAll as SelectAllIcon,
  ClearAll as ClearAllIcon,
  DeleteSweep as DeleteSweepIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  Psychology as PsychologyIcon,
  LocalOffer as LocalOfferIcon,
  Notes as NotesIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { format, isToday, isYesterday, isThisWeek, isThisMonth } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Trade } from '../../contexts/TradingJournalContext';
import TradeForm from './TradeForm';

interface TradesListProps {
  trades: Trade[];
  onUpdateTrade: (id: string, updates: Partial<Trade>) => Promise<void>;
  onDeleteTrade: (id: string) => Promise<void>;
  onDeleteTrades: (ids: string[]) => Promise<void>;
  loading: boolean;
}

type SortField = 'entryTime' | 'exitTime' | 'symbol' | 'pnl' | 'broker';
type SortDirection = 'asc' | 'desc';

const TradesList: React.FC<TradesListProps> = ({
  trades,
  onUpdateTrade,
  onDeleteTrade,
  onDeleteTrades,
  loading
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingBatchDelete, setLoadingBatchDelete] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    broker: '',
    status: '',
    symbol: '',
    strategy: '',
    timeframe: '',
    emotion: '',
    dateRange: '',
    searchQuery: '',
  });
  
  // Sort states
  const [sortField, setSortField] = useState<SortField>('entryTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, trade: Trade) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrade(trade);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTrade(null);
  };

  const handleEdit = () => {
    // Close menu but keep selectedTrade
    setAnchorEl(null);
    
    // Show loading immediately
    setLoadingEdit(true);
    
    // Use requestAnimationFrame to ensure the loading state is rendered
    // before starting the timeout
    requestAnimationFrame(() => {
      setTimeout(() => {
        setEditDialogOpen(true);
        setLoadingEdit(false);
      }, 800);
    });
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedTrade
  };

  const handleViewDetails = () => {
    setDetailDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedTrade
  };

  const confirmDelete = async () => {
    if (selectedTrade) {
      setLoadingDelete(true);
      try {
        await onDeleteTrade(selectedTrade.id);
        setDeleteDialogOpen(false);
        setSelectedTrade(null);
      } catch (error) {
        console.error('Error deleting trade:', error);
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  const handleUpdateTrade = async (updates: Partial<Trade>) => {
    if (selectedTrade) {
      try {
        await onUpdateTrade(selectedTrade.id, updates);
        setEditDialogOpen(false);
        setSelectedTrade(null);
        setLoadingEdit(false);
      } catch (error) {
        console.error('Error updating trade:', error);
        setLoadingEdit(false);
      }
    }
  };

  // Multiselect handlers
  const handleToggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedTrades([]);
  };

  const handleSelectTrade = (tradeId: string) => {
    setSelectedTrades(prev => 
      prev.includes(tradeId)
        ? prev.filter(id => id !== tradeId)
        : [...prev, tradeId]
    );
  };

  const handleSelectAllTrades = () => {
    if (selectedTrades.length === filteredAndSortedTrades.length) {
      setSelectedTrades([]);
    } else {
      setSelectedTrades(filteredAndSortedTrades.map(trade => trade.id));
    }
  };

  const handleBatchDelete = () => {
    if (selectedTrades.length > 0) {
      setBatchDeleteDialogOpen(true);
    }
  };

  const confirmBatchDelete = async () => {
    setLoadingBatchDelete(true);
    try {
      await onDeleteTrades(selectedTrades);
      setBatchDeleteDialogOpen(false);
      setSelectedTrades([]);
      setSelectionMode(false);
    } catch (error) {
      console.error('Error deleting trades:', error);
    } finally {
      setLoadingBatchDelete(false);
    }
  };

  const filteredAndSortedTrades = useMemo(() => {
    let filtered = trades.filter(trade => {
      // Broker filter
      if (filters.broker && trade.broker !== filters.broker) return false;
      
      // Status filter
      if (filters.status && trade.status !== filters.status) return false;
      
      // Symbol filter
      if (filters.symbol && !trade.symbol.toLowerCase().includes(filters.symbol.toLowerCase())) return false;
      
      // Strategy filter
      if (filters.strategy && trade.strategy !== filters.strategy) return false;
      
      // Timeframe filter
      if (filters.timeframe && trade.timeframe !== filters.timeframe) return false;
      
      // Emotion filter
      if (filters.emotion && trade.emotion !== filters.emotion) return false;
      
      // Date range filter
      if (filters.dateRange) {
        const tradeDate = new Date(trade.entryTime);
        switch (filters.dateRange) {
          case 'today':
            if (!isToday(tradeDate)) return false;
            break;
          case 'yesterday':
            if (!isYesterday(tradeDate)) return false;
            break;
          case 'thisWeek':
            if (!isThisWeek(tradeDate)) return false;
            break;
          case 'thisMonth':
            if (!isThisMonth(tradeDate)) return false;
            break;
        }
      }
      
      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const searchableText = [
          trade.symbol,
          trade.strategy,
          trade.notes,
          trade.setup,
          ...(trade.tags || [])
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(query)) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'entryTime':
          aValue = new Date(a.entryTime).getTime();
          bValue = new Date(b.entryTime).getTime();
          break;
        case 'exitTime':
          aValue = a.exitTime ? new Date(a.exitTime).getTime() : 0;
          bValue = b.exitTime ? new Date(b.exitTime).getTime() : 0;
          break;
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'pnl':
          aValue = a.pnl || 0;
          bValue = b.pnl || 0;
          break;
        case 'broker':
          aValue = a.broker;
          bValue = b.broker;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [trades, filters, sortField, sortDirection]);

  const getDateLabel = (date: Date) => {
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    if (isThisWeek(date)) return format(date, 'EEEE');
    return format(date, 'MMM d, yyyy');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'info';
      case 'closed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getEmotionColor = (emotion: string) => {
    switch (emotion) {
      case 'confident': return 'success';
      case 'disciplined': return 'primary';
      case 'neutral': return 'default';
      case 'fearful': return 'warning';
      case 'greedy': return 'error';
      default: return 'default';
    }
  };

  const uniqueValues = {
    brokers: Array.from(new Set(trades.map(t => t.broker))),
    strategies: Array.from(new Set(trades.map(t => t.strategy).filter(Boolean))),
    timeframes: Array.from(new Set(trades.map(t => t.timeframe).filter(Boolean))),
    emotions: Array.from(new Set(trades.map(t => t.emotion).filter(Boolean))),
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography>Loading trades...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters and Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={showFilters ? 2 : 0}>
          <Typography variant="h6" fontWeight="bold">
            Trades ({filteredAndSortedTrades.length})
            {selectionMode && selectedTrades.length > 0 && (
              <Chip 
                label={`${selectedTrades.length} selected`} 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            )}
          </Typography>
          
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={selectionMode ? <ClearAllIcon /> : <SelectAllIcon />}
              onClick={handleToggleSelectionMode}
              variant={selectionMode ? 'contained' : 'outlined'}
              size="small"
              color={selectionMode ? 'secondary' : 'primary'}
            >
              {selectionMode ? 'Cancel' : 'Select'}
            </Button>
            
            <Button
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'contained' : 'outlined'}
              size="small"
            >
              Filters
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={`${sortField}-${sortDirection}`}
                onChange={(e) => {
                  const [field, direction] = e.target.value.split('-');
                  setSortField(field as SortField);
                  setSortDirection(direction as SortDirection);
                }}
                label="Sort by"
              >
                <MenuItem value="entryTime-desc">Newest First</MenuItem>
                <MenuItem value="entryTime-asc">Oldest First</MenuItem>
                <MenuItem value="pnl-desc">Highest P&L</MenuItem>
                <MenuItem value="pnl-asc">Lowest P&L</MenuItem>
                <MenuItem value="symbol-asc">Symbol A-Z</MenuItem>
                <MenuItem value="symbol-desc">Symbol Z-A</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Selection Mode Toolbar */}
        {selectionMode && (
          <Toolbar 
            sx={{ 
              minHeight: '48px !important', 
              bgcolor: 'action.hover', 
              borderRadius: 1,
              px: 2,
              py: 1,
              mb: 2
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTrades.length === filteredAndSortedTrades.length && filteredAndSortedTrades.length > 0}
                    indeterminate={selectedTrades.length > 0 && selectedTrades.length < filteredAndSortedTrades.length}
                    onChange={handleSelectAllTrades}
                  />
                }
                label="Select All"
              />
              
              <Box sx={{ flexGrow: 1 }} />
              
              {selectedTrades.length > 0 && (
                <Button
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleBatchDelete}
                  variant="contained"
                  color="error"
                  size="small"
                >
                  Delete Selected ({selectedTrades.length})
                </Button>
              )}
            </Stack>
          </Toolbar>
        )}
        
        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={filters.searchQuery}
                onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                placeholder="Symbol, strategy, notes..."
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Broker</InputLabel>
                <Select
                  value={filters.broker}
                  onChange={(e) => setFilters(prev => ({ ...prev, broker: e.target.value }))}
                  label="Broker"
                >
                  <MenuItem value="">All Brokers</MenuItem>
                  {uniqueValues.brokers.map(broker => (
                    <MenuItem key={broker} value={broker}>{broker}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  label="Date Range"
                >
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Trades List */}
      {filteredAndSortedTrades.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {trades.length === 0 
            ? "No trades found. Start by adding your first trade!" 
            : "No trades match your current filters."}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          <AnimatePresence>
            {filteredAndSortedTrades.map((trade, index) => (
              <Grid item xs={12} md={6} lg={4} key={trade.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      borderRadius: 2, 
                      boxShadow: 2,
                      '&:hover': { boxShadow: 4 },
                      border: trade.status === 'open' ? 2 : 1,
                      borderColor: trade.status === 'open' ? 'primary.main' : 'divider',
                    }}
                  >
                    <CardContent>
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box display="flex" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
                          {selectionMode && (
                            <Checkbox
                              checked={selectedTrades.includes(trade.id)}
                              onChange={() => handleSelectTrade(trade.id)}
                              sx={{ p: 0.5 }}
                            />
                          )}
                          
                          <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: trade.type === 'buy' ? 'success.main' : 'error.main',
                                  fontSize: 12,
                                }}
                              >
                                {trade.type === 'buy' ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                              </Avatar>
                              <Typography variant="h6" fontWeight="bold">
                                {trade.symbol}
                              </Typography>
                              <Chip
                                label={trade.broker}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary">
                              {getDateLabel(new Date(trade.entryTime))} • {format(new Date(trade.entryTime), 'HH:mm')}
                            </Typography>
                          </Box>
                          
                          {!selectionMode && (
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, trade)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* Trade Details */}
                      <Grid container spacing={1} mb={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Entry Price
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {trade.entryPrice.toFixed(5)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Quantity
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {trade.quantity}
                          </Typography>
                        </Grid>
                        
                        {trade.exitPrice && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Exit Price
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {trade.exitPrice.toFixed(5)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>

                      {/* P&L and Status */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Chip
                          label={trade.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(trade.status) as any}
                          variant="filled"
                        />
                        
                        {trade.pnl !== undefined && (
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color={trade.pnl >= 0 ? 'success.main' : 'error.main'}
                          >
                            {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                          </Typography>
                        )}
                      </Box>

                      {/* Additional Info */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {trade.strategy && (
                          <Chip
                            label={trade.strategy}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                        
                        {trade.emotion && (
                          <Chip
                            label={trade.emotion}
                            size="small"
                            variant="outlined"
                            color={getEmotionColor(trade.emotion) as any}
                          />
                        )}
                        
                        {trade.timeframe && (
                          <Chip
                            label={trade.timeframe}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {trade.notes && (
                        <Box mt={2}>
                          <Typography variant="body2" color="text.secondary" sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {trade.notes}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      {selectedTrade && (
        <TradeForm
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedTrade(null);
            setLoadingEdit(false);
          }}
          onSubmit={handleUpdateTrade}
          initialData={selectedTrade}
          mode="edit"
        />
      )}

      {/* Delete Confirmation */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !loadingDelete && setDeleteDialogOpen(false)}
        disableEscapeKeyDown={loadingDelete}
      >
        <DialogTitle>Confirm Delete Trade</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this trade? This action cannot be undone.
          </Typography>
          {selectedTrade && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {selectedTrade.symbol} - {selectedTrade.type.toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(selectedTrade.entryTime), 'MMM d, yyyy HH:mm')}
              </Typography>
            </Box>
          )}
          
          {loadingDelete && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Deleting trade...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loadingDelete}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={loadingDelete}
            startIcon={loadingDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {loadingDelete ? 'Deleting...' : 'Delete Trade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Delete Confirmation */}
      <Dialog open={batchDeleteDialogOpen} onClose={() => setBatchDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Batch Delete</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete {selectedTrades.length} selected trades? This action cannot be undone.
          </Typography>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will permanently delete all selected trades from your trading journal.
            </Typography>
          </Alert>
          
          {selectedTrades.length <= 5 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Trades:
              </Typography>
              {selectedTrades.map(tradeId => {
                const trade = trades.find(t => t.id === tradeId);
                return trade ? (
                  <Box key={tradeId} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {trade.symbol} - {trade.type.toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(trade.entryTime), 'MMM d, yyyy HH:mm')}
                    </Typography>
                  </Box>
                ) : null;
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBatchDeleteDialogOpen(false)}
            disabled={loadingBatchDelete}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmBatchDelete} 
            color="error" 
            variant="contained"
            disabled={loadingBatchDelete}
            startIcon={loadingBatchDelete ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
          >
            {loadingBatchDelete ? 'Deleting...' : `Delete ${selectedTrades.length} Trades`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trade Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedTrade(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: selectedTrade?.type === 'buy' ? 'success.main' : 'error.main',
                  width: 40,
                  height: 40,
                }}
              >
                {selectedTrade?.type === 'buy' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {selectedTrade?.symbol} - {selectedTrade?.type.toUpperCase()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Trade Details
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => {
              setDetailDialogOpen(false);
              setSelectedTrade(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedTrade && (
            <Grid container spacing={3}>
              {/* Trade Overview */}
              <Grid item xs={12}>
                <Card elevation={1} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={selectedTrade.status.toUpperCase()}
                            size="small"
                            color={getStatusColor(selectedTrade.status) as any}
                            variant="filled"
                            sx={{ display: 'block', width: 'fit-content', mt: 0.5 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Broker
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTrade.broker}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Quantity
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTrade.quantity}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            P&L
                          </Typography>
                          {selectedTrade.pnl !== undefined ? (
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color={selectedTrade.pnl >= 0 ? 'success.main' : 'error.main'}
                            >
                              {selectedTrade.pnl >= 0 ? '+' : ''}${selectedTrade.pnl.toFixed(2)}
                            </Typography>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Price Information */}
              <Grid item xs={12} md={6}>
                <Card elevation={1}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <AttachMoneyIcon color="primary" />
                      <Typography variant="h6">
                        Price Information
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Entry Price
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {selectedTrade.entryPrice.toFixed(5)}
                        </Typography>
                      </Box>
                      
                      {selectedTrade.exitPrice && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Exit Price
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {selectedTrade.exitPrice.toFixed(5)}
                          </Typography>
                        </Box>
                      )}

                      {selectedTrade.stopLoss && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Stop Loss
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="error.main">
                            {selectedTrade.stopLoss.toFixed(5)}
                          </Typography>
                        </Box>
                      )}

                      {selectedTrade.takeProfit && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Take Profit
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" color="success.main">
                            {selectedTrade.takeProfit.toFixed(5)}
                          </Typography>
                        </Box>
                      )}

                      <Divider />
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Commission
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ${selectedTrade.commission.toFixed(2)}
                        </Typography>
                      </Box>

                      {selectedTrade.swap && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Swap
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            ${selectedTrade.swap.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Timing Information */}
              <Grid item xs={12} md={6}>
                <Card elevation={1}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <ScheduleIcon color="primary" />
                      <Typography variant="h6">
                        Timing
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Entry Time
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {format(new Date(selectedTrade.entryTime), 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(selectedTrade.entryTime), 'HH:mm:ss')}
                        </Typography>
                      </Box>

                      {selectedTrade.exitTime && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Exit Time
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {format(new Date(selectedTrade.exitTime), 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(selectedTrade.exitTime), 'HH:mm:ss')}
                          </Typography>
                        </Box>
                      )}

                      {selectedTrade.exitTime && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {(() => {
                              const duration = new Date(selectedTrade.exitTime).getTime() - new Date(selectedTrade.entryTime).getTime();
                              const days = Math.floor(duration / (1000 * 60 * 60 * 24));
                              const hours = Math.floor((duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                              const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
                              
                              if (days > 0) return `${days}d ${hours}h ${minutes}m`;
                              if (hours > 0) return `${hours}h ${minutes}m`;
                              return `${minutes}m`;
                            })()}
                          </Typography>
                        </Box>
                      )}

                      <Divider />

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(selectedTrade.createdAt), 'MMM d, yyyy HH:mm')}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Last Updated
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(selectedTrade.updatedAt), 'MMM d, yyyy HH:mm')}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Strategy & Psychology */}
              <Grid item xs={12} md={6}>
                <Card elevation={1}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <PsychologyIcon color="primary" />
                      <Typography variant="h6">
                        Strategy & Psychology
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      {selectedTrade.strategy && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Strategy
                          </Typography>
                          <Chip
                            label={selectedTrade.strategy}
                            variant="outlined"
                            color="secondary"
                            sx={{ display: 'block', width: 'fit-content', mt: 0.5 }}
                          />
                        </Box>
                      )}

                      {selectedTrade.setup && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Setup
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTrade.setup}
                          </Typography>
                        </Box>
                      )}

                      {selectedTrade.timeframe && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Timeframe
                          </Typography>
                          <Chip
                            label={selectedTrade.timeframe}
                            variant="outlined"
                            size="small"
                            sx={{ display: 'block', width: 'fit-content', mt: 0.5 }}
                          />
                        </Box>
                      )}

                      {selectedTrade.emotion && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Emotion
                          </Typography>
                          <Chip
                            label={selectedTrade.emotion}
                            variant="outlined"
                            color={getEmotionColor(selectedTrade.emotion) as any}
                            size="small"
                            sx={{ display: 'block', width: 'fit-content', mt: 0.5 }}
                          />
                        </Box>
                      )}

                      {selectedTrade.confidence && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Confidence Level
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Typography variant="body1" fontWeight="medium">
                              {selectedTrade.confidence}/5
                            </Typography>
                            <Box display="flex" gap={0.5}>
                              {[1, 2, 3, 4, 5].map((level) => (
                                <Box
                                  key={level}
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: level <= selectedTrade.confidence! 
                                      ? 'primary.main' 
                                      : 'action.disabled',
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {selectedTrade.marketCondition && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Market Condition
                          </Typography>
                          <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                            {selectedTrade.marketCondition}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tags & Notes */}
              <Grid item xs={12} md={6}>
                <Card elevation={1}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <NotesIcon color="primary" />
                      <Typography variant="h6">
                        Notes & Tags
                      </Typography>
                    </Box>
                    <Stack spacing={2}>
                      {selectedTrade.tags && selectedTrade.tags.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Tags
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedTrade.tags.map((tag, index) => (
                              <Chip
                                key={index}
                                label={tag}
                                size="small"
                                variant="outlined"
                                icon={<LocalOfferIcon fontSize="small" />}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {selectedTrade.notes && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Notes
                          </Typography>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 2, 
                              mt: 1, 
                              bgcolor: 'action.hover',
                              borderRadius: 1 
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedTrade.notes}
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {(!selectedTrade.tags || selectedTrade.tags.length === 0) && !selectedTrade.notes && (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          No notes or tags added for this trade.
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedTrade(null);
          }}>
            Close
          </Button>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setDetailDialogOpen(false);
              handleDelete();
            }}
            sx={{ mr: 1 }}
          >
            Delete Trade
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setDetailDialogOpen(false);
              handleEdit();
            }}
          >
            Edit Trade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Modal Overlay */}
      <Backdrop
        open={loadingEdit}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={60} color="primary" />
          <Typography variant="h6" color="white" fontWeight="medium">
            Loading Trade Editor...
          </Typography>
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
            Please wait while we prepare your trade data
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default TradesList; 